import styled from '@emotion/styled';
import Close from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';

import { CounterpartSelectForm } from './CounterpartSelectForm';
import { DestinationStoreForm } from './DestinationStoreForm';
import { InvoiceNumberForm } from './InvoiceNumberForm';

export const OperationVoucherType = Object.freeze({
  NO_VOUCHER: 0,
  DOMESTIC_PURCHASE: 1,
  COMMERCIAL_DOMESTIC_PURCHASE: 2,
});

const STEPS = Object.freeze({
  SELECT: 0,
  COUNTERPART: 1,
  INVOICE: 2,
  STORE: 3,
  DETAILS: 4,
});

const voucherTypeOptions = [
  {
    type: OperationVoucherType.DOMESTIC_PURCHASE,
    label: 'خرید داخلی',
    enabled: false,
  },
  {
    type: OperationVoucherType.COMMERCIAL_DOMESTIC_PURCHASE,
    label: 'خرید داخلی (بازرگانی)',
    enabled: false,
  },
  {
    type: OperationVoucherType.NO_VOUCHER,
    label: 'بدون سند',
    enabled: true,
  },
];

const stepTitles = {
  [STEPS.SELECT]: 'بخش مورد نظر را انتخاب کنید',
  [STEPS.COUNTERPART]: 'فروشنده را انتخاب کنید',
  [STEPS.INVOICE]: 'شماره فاکتور را وارد کنید',
  [STEPS.STORE]: 'انبار مقصد را انتخاب کنید',
  [STEPS.DETAILS]: 'اطلاعات مورد نظر را وارد کنید',
};

const detailsDefaultValues = {
  title: '',
  description: '',
};

const toIntOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const TransferOperationInsertModal = ({
  open,
  handleClose,
  insertLoading,
  onInsert,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const [step, setStep] = useState(STEPS.SELECT);
  const [voucherType, setVoucherType] = useState();
  const [voucherData, setVoucherData] = useState({});

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors, isSubmitted },
  } = useForm({ defaultValues: detailsDefaultValues });

  useEffect(() => {
    if (!open) {
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      setVoucherData({});
      reset(detailsDefaultValues);
    }
  }, [open, reset]);

  const handleSelectVoucherType = type => {
    setVoucherType(type);
    setVoucherData({});

    if (type === OperationVoucherType.DOMESTIC_PURCHASE) {
      setStep(STEPS.COUNTERPART);
      return;
    }

    if (type === OperationVoucherType.COMMERCIAL_DOMESTIC_PURCHASE) {
      setStep(STEPS.INVOICE);
      return;
    }

    setStep(STEPS.DETAILS);
  };

  const handleCounterpartSubmit = data => {
    setVoucherData(prev => ({ ...prev, ...data }));
    setStep(STEPS.INVOICE);
  };

  const handleInvoiceSubmit = data => {
    setVoucherData(prev => ({ ...prev, ...data }));
    setStep(STEPS.STORE);
  };

  const handleStoreSubmit = data => {
    setVoucherData(prev => ({ ...prev, ...data }));
    setStep(STEPS.DETAILS);
  };

  const handleDetailsSubmit = data => {
    onInsert?.({
      zoneGuid: operationSettings?.defaultZone?.key,
      title: data.title?.trim() ?? '',
      description: data.description?.trim() ?? '',
      voucherType: Number(voucherType),
      counterpartID: toIntOrNull(voucherData.counterpartID),
      voucherID: toIntOrNull(voucherData.voucherID),
      invoiceNumber: toIntOrNull(voucherData.invoiceNumber),
      storeID: toIntOrNull(voucherData.storeID) ?? 0,
    });
  };

  const handleBack = () => {
    if (step === STEPS.DETAILS) {
      if (
        voucherType === OperationVoucherType.DOMESTIC_PURCHASE ||
        voucherType === OperationVoucherType.COMMERCIAL_DOMESTIC_PURCHASE
      ) {
        setStep(STEPS.STORE);
        return;
      }
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      return;
    }

    if (step === STEPS.STORE) {
      setStep(STEPS.INVOICE);
      return;
    }

    if (step === STEPS.INVOICE) {
      if (voucherType === OperationVoucherType.DOMESTIC_PURCHASE) {
        setStep(STEPS.COUNTERPART);
        return;
      }
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      setVoucherData({});
      return;
    }

    if (step === STEPS.COUNTERPART) {
      setStep(STEPS.SELECT);
      setVoucherType(undefined);
      setVoucherData({});
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ direction: 'rtl', minWidth: '360px' }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 16,
        }}
      >
        {stepTitles[step]}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {step === STEPS.SELECT ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              pt: 2,
              minWidth: 320,
            }}
          >
            {voucherTypeOptions
              .filter(option => option.enabled)
              .map(option => (
                <OptionButton
                  key={option.type}
                  fullWidth
                  onClick={() => handleSelectVoucherType(option.type)}
                >
                  {option.label}
                </OptionButton>
              ))}
          </Box>
        ) : null}

        {step === STEPS.COUNTERPART ? (
          <CounterpartSelectForm
            onSubmit={handleCounterpartSubmit}
            onBack={handleBack}
          />
        ) : null}

        {step === STEPS.INVOICE ? (
          <InvoiceNumberForm
            onSubmit={handleInvoiceSubmit}
            onBack={handleBack}
          />
        ) : null}

        {step === STEPS.STORE ? (
          <DestinationStoreForm
            onSubmit={handleStoreSubmit}
            onBack={handleBack}
          />
        ) : null}

        {step === STEPS.DETAILS ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              pt: 1,
              minWidth: 300,
            }}
          >
            <FormControl variant="standard" sx={{ m: 2, minWidth: 280 }}>
              <TextField
                autoFocus
                label="عنوان"
                variant="standard"
                {...register('title', { required: 'عنوان الزامی است' })}
                error={isSubmitted ? !!errors.title : false}
                helperText={isSubmitted ? errors.title?.message : null}
              />
            </FormControl>

            <FormControl variant="standard" sx={{ m: 2, minWidth: 280 }}>
              <TextField
                label="توضیحات"
                variant="standard"
                multiline
                minRows={3}
                {...register('description')}
              />
            </FormControl>
          </Box>
        ) : null}
      </DialogContent>

      {step === STEPS.DETAILS ? (
        <DialogActions sx={{ gap: 1, padding: 3 }}>
          <Button
            onClick={handleBack}
            variant="contained"
            disabled={insertLoading}
          >
            بازگشت
          </Button>
          <LoadingButton
            color="warning"
            variant="contained"
            onClick={handleSubmit(handleDetailsSubmit)}
            loading={insertLoading}
          >
            ثبت عملیات
          </LoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default TransferOperationInsertModal;

const OptionButton = styled(Button)`
  background-color: #e8e8e8;
  color: #333;
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 500;
  box-shadow: none;

  &:hover {
    background-color: #dcdcdc;
    box-shadow: none;
  }
`;
